import React, { useState, useEffect } from 'react';
import { Button, Modal, Input, Card, List, Popconfirm, Spin, Tag, Space, message, DatePicker, TimePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, MailOutlined, MessageOutlined, BellOutlined, WhatsAppOutlined, CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import drEasyApi from '../../../Api/Customapi';

const { TextArea } = Input;

const AdsScreen = () => {
    // State declarations
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [editingAd, setEditingAd] = useState(null);
    const [viewingAd, setViewingAd] = useState(null);

    // Form fields
    const [title, setTitle] = useState('');
    const [titleAr, setTitleAr] = useState('');
    const [description, setDescription] = useState('');
    const [descriptionAr, setDescriptionAr] = useState('');
    const [sendDate, setSendDate] = useState(null); // Changed to use dayjs object
    const [sendTime, setSendTime] = useState(null); // Changed to use dayjs object
    const [sendMethod, setSendMethod] = useState(null);

    // Send methods configuration
    const sendMethods = [
        { key: 1, value: 'Email', icon: 'email' },
        // { key: 2, value: 'SMS', icon: 'sms' },
        { key: 3, value: 'Push Notification', icon: 'notification' },
        // { key: 4, value: 'WhatsApp', icon: 'whatsapp' }
    ];

    useEffect(() => {
        fetchAds();
    }, []);

    const fetchAds = async () => {
        try {
            setLoading(true);
            const res = await drEasyApi.get("/api/Advertisements/GetAll");
            console.log('Fetched ads:', res);
            setAds(res.data.items || []);
        } catch (err) {
            console.error('Error fetching ads:', err);
            message.error(err?.response?.data?.[0] || 'Failed to fetch ads');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setTitle('');
        setDescription('');
        setSendDate(null);
        setSendTime(null);
        setSendMethod(null);
        setEditingAd(null);
    };

    const handleAdd = () => {
        resetForm();
        setIsModalVisible(true);
    };

    const handleEdit = (ad) => {
        setEditingAd(ad);
        setTitle(ad.title || '');
        setDescription(ad.description || '');

        if (ad.sendDateTime) {
            const sendDateTime = dayjs(ad.sendDateTime);
            setSendDate(sendDateTime);
            setSendTime(sendDateTime);
        }

        setSendMethod(ad.sendMethod);
        setIsModalVisible(true);
    };

    const handleViewDetails = (ad) => {
        setViewingAd(ad);
        setIsViewModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await drEasyApi.delete(`/api/Advertisements/Delete?Id=${id}`);
            message.success('Ad deleted successfully');
            fetchAds();
        } catch (err) {
            console.error('Error deleting ad:', err);
            message.error(err?.response?.data?.[0] || 'Failed to delete ad');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!title || !description || !sendDate || !sendTime || !sendMethod) {
            message.error('Please fill all required fields');
            return;
        }

        // Combine date and time
        const combinedDateTime = dayjs(sendDate)
            .hour(sendTime.hour())
            .minute(sendTime.minute())
            .second(0)
            .format('YYYY-MM-DDTHH:mm:ss') + 'Z';

        const adData = {
            title,
            description,
            sendDateTime: combinedDateTime,
            sendMethod,
        };

        try {
            setLoading(true);

            if (editingAd) {
                await drEasyApi.put("/api/Advertisements/Edit", {
                    id: editingAd.id,
                    ...adData
                });
                message.success('Ad updated successfully');
            } else {
                await drEasyApi.post("/api/Advertisements/Add", adData);
                message.success('Ad created successfully');
            }

            fetchAds();
            setIsModalVisible(false);
            resetForm();
        } catch (err) {
            console.error('Error submitting ad:', err);
            message.error(err?.response?.data?.[0] || 'Failed to save ad');
        } finally {
            setLoading(false);
        }
    };

    const getSendMethodIcon = (method) => {
        const icons = {
            1: <MailOutlined style={{ color: '#1890ff' }} />,
            2: <MessageOutlined style={{ color: '#52c41a' }} />,
            3: <BellOutlined style={{ color: '#faad14' }} />,
            4: <WhatsAppOutlined style={{ color: '#25D366' }} />
        };
        return icons[method] || null;
    };

    const getSendMethodLabel = (method) => {
        const labels = {
            1: 'Email',
            2: 'SMS',
            3: 'Push Notification',
            4: 'WhatsApp'
        };
        return labels[method] || 'Unknown';
    };

    const truncateDescription = (description, maxLength = 150) => {
        if (!description || description.length <= maxLength) {
            return description;
        }
        return description.substring(0, maxLength) + '...';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(`${dateStr}Z`);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const getAdStatus = (sendDateTime) => {
        if (!sendDateTime) return 'scheduled';
        const now = new Date();
        const sendDate = new Date(sendDateTime);
        return sendDate <= now ? 'sent' : 'scheduled';
    };

    // Disable dates before today
    const disabledDate = (current) => {
        return current && current < dayjs().startOf('day');
    };

    return (
        <div className="md:p-6">
            <div className="flex flex-row justify-between md:items-center mb-4">
                <h2 className="text-lg md:text-xl font-semibold">Campaign Ads Management</h2>
                <Button type=""
                    size="large"
                    className="bg-gradient-to-r !text-xs !p-2 md:p-4 md:!text-lg text-white from-primary to-primary_hover hover:bg-primary_hover hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
                    icon={<PlusOutlined />} onClick={handleAdd}>
                    Create New Ad
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center items-center min-h-[400px]">
                    <Spin size="large" />
                </div>
            ) : (
                <List
                    grid={{
                        gutter: 16,
                        xs: 1,
                        sm: 1,
                        md: 2,
                        lg: 2,
                        xl: 3,
                        xxl: 3,
                    }}
                    dataSource={ads}
                    renderItem={(item) => {
                        const status = getAdStatus(item.sendDateTime);
                        return (
                            <List.Item>
                                <Card
                                    className="overflow-hidden shadow-md border border-[#298d9f73] hover:shadow-lg transition-all rounded-lg"
                                    actions={[
                                        <Button
                                            icon={<EyeOutlined />}
                                            onClick={() => handleViewDetails(item)}
                                            type="text"
                                            key="view"
                                        />,
                                        <Button
                                            icon={<EditOutlined />}
                                            onClick={() => handleEdit(item)}
                                            type="text"
                                            key="edit"
                                        />,
                                        <Popconfirm
                                            title="Are you sure to delete this ad?"
                                            onConfirm={() => handleDelete(item.id)}
                                            key="delete"
                                        >
                                            <Button danger icon={<DeleteOutlined />} type="text" />
                                        </Popconfirm>,
                                    ]}
                                >
                                    <div className="mb-3">
                                        <Tag color={status === 'sent' ? 'success' : 'processing'} className="mb-2">
                                            {status === 'sent' ? 'Sent' : 'Scheduled'}
                                        </Tag>
                                    </div>

                                    <Card.Meta
                                        title={
                                            <div className="flex items-center gap-2 text-lg font-semibold text-[#298d9f]">
                                                {item.title}
                                            </div>
                                        }
                                        description={
                                            <div>
                                                <p className="text-sm text-gray-600 mb-3">
                                                    {truncateDescription(item.description)}
                                                </p>

                                                <div className="space-y-2 mb-3">
                                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                                        <CalendarOutlined />
                                                        <span>{formatDate(item.sendDateTime)}</span>
                                                        <ClockCircleOutlined className="ml-2" />
                                                        <span>{formatTime(item.sendDateTime)}</span>
                                                    </div>
                                                </div>

                                                <div className="flex gap-1 flex-wrap">
                                                    <Tag
                                                        icon={getSendMethodIcon(item.sendMethod)}
                                                        className="flex items-center gap-1"
                                                    >
                                                        {getSendMethodLabel(item.sendMethod)}
                                                    </Tag>
                                                </div>

                                                {item.description && item.description.length > 150 && (
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        onClick={() => handleViewDetails(item)}
                                                        className="p-0 mt-2 underline text-[#298d9f] hover:text-[#1f6b7a]"
                                                    >
                                                        View full details
                                                    </Button>
                                                )}
                                            </div>
                                        }
                                    />
                                </Card>
                            </List.Item>
                        );
                    }}
                />
            )}
            <Modal
                title={editingAd ? 'Edit Campaign Ad' : 'Create New Campaign Ad'}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    resetForm();
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setIsModalVisible(false);
                        resetForm();
                    }}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSubmit}
                        loading={loading}
                        style={{ backgroundColor: '#298d9f', borderColor: '#298d9f' }}
                    >
                        {editingAd ? 'Update' : 'Create'}
                    </Button>,
                ]}
                width={700}
                destroyOnClose
            >
                <div className="space-y-4 mt-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Campaign Title *</label>
                        <Input
                            placeholder="e.g., Spring Health Checkup Campaign"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>


                    <div>
                        <label className="block text-sm font-medium mb-1">Description *</label>
                        <TextArea
                            rows={4}
                            placeholder="Describe your campaign message..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>


                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Send Date *</label>
                            <DatePicker
                                value={sendDate}
                                onChange={(date) => setSendDate(date)}
                                format="YYYY-MM-DD"
                                disabledDate={disabledDate}
                                className="w-full"
                                placeholder="Select date"
                                suffixIcon={<CalendarOutlined />}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Send Time *</label>
                            <TimePicker
                                value={sendTime}
                                onChange={(time) => setSendTime(time)}
                                className="w-full"

                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">Delivery Method *</label>
                        <div className="grid grid-cols-2 gap-3">
                            {sendMethods.map((method) => (
                                <div
                                    key={method.key}
                                    onClick={() => setSendMethod(method.key)}
                                    className={`border rounded p-3 cursor-pointer transition-all ${sendMethod === method.key
                                            ? 'border-[#298d9f] bg-[#298d9f10]'
                                            : 'border-gray-300 hover:border-[#298d9f]'
                                        }`}
                                >
                                    <Space>
                                        {getSendMethodIcon(method.key)}
                                        <span>{method.value}</span>
                                    </Space>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal
                title="Campaign Ad Details"
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsViewModalVisible(false)}>
                        Close
                    </Button>,
                ]}
                width={700}
                destroyOnClose
            >
                {viewingAd && (
                    <div>
                        <div className="mb-4">
                            <Tag
                                color={getAdStatus(viewingAd.sendDateTime) === 'sent' ? 'success' : 'processing'}
                                className="text-sm px-3 py-1"
                            >
                                {getAdStatus(viewingAd.sendDateTime) === 'sent' ? 'Sent' : 'Scheduled'}
                            </Tag>
                        </div>

                        <h3 className="text-xl font-semibold text-[#298d9f] mb-2">
                            {viewingAd.title}
                        </h3>



                        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                    <CalendarOutlined className="text-[#298d9f]" />
                                    <span className="font-medium">Date:</span>
                                    <span>{formatDate(viewingAd.sendDateTime)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <ClockCircleOutlined className="text-[#298d9f]" />
                                    <span className="font-medium">Time:</span>
                                    <span>{formatTime(viewingAd.sendDateTime)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <h4 className="font-medium mb-2 text-gray-700">Delivery Method:</h4>
                            <div className="flex gap-2 flex-wrap">
                                <Tag
                                    icon={getSendMethodIcon(viewingAd.sendMethod)}
                                    className="flex items-center gap-1 text-sm px-3 py-1"
                                >
                                    {getSendMethodLabel(viewingAd.sendMethod)}
                                </Tag>
                            </div>
                        </div>

                        <div className="text-gray-700 mb-4">
                            <h4 className="font-medium mb-2">Description:</h4>
                            <p className="text-sm leading-relaxed whitespace-pre-wrap bg-gray-50 p-3 rounded">
                                {viewingAd.description}
                            </p>
                        </div>


                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AdsScreen;